import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Calendar, TrendingUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const StaffStatsPage = () => {
  const { token } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffStats, setStaffStats] = useState([]);
  const [dateFilter, setDateFilter] = useState("year");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get("/patients", token);
        setPatients(response.data);
      } catch (error) {
        toast.error("خطأ في جلب البيانات");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  useEffect(() => {
    if (!patients.length) { setStaffStats([]); return; }

    const now = new Date();
    let filterStart = "";
    let filterEnd = now.toISOString().split("T")[0];

    if (dateFilter === "year") {
      filterStart = `${now.getFullYear()}-01-01`;
    } else if (dateFilter === "month") {
      filterStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    } else if (dateFilter === "lastMonth") {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      filterStart = lastMonth.toISOString().split("T")[0];
      filterEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0];
    } else if (dateFilter === "range") {
      filterStart = startDate;
      filterEnd = endDate || filterEnd;
    } else {
      filterStart = "";
      filterEnd = "";
    }

    const filtered = patients.filter(p => {
      const opDate = p.operation_date || p.transplant_date || "";
      if (!opDate) return false;
      if (filterStart && opDate < filterStart) return false;
      if (filterEnd && opDate > filterEnd) return false;
      return true;
    });

    const stats = {};
    filtered.forEach(p => {
      if (p.extraction_by) {
        const key = p.extraction_by_other || p.extraction_by;
        if (!stats[key]) stats[key] = { name: key, extractions: 0, channels: 0, planting: 0, total: 0 };
        stats[key].extractions++;
        stats[key].total++;
      }
      if (p.channel_opening_by) {
        const key = p.channel_opening_by_other || p.channel_opening_by;
        if (!stats[key]) stats[key] = { name: key, extractions: 0, channels: 0, planting: 0, total: 0 };
        stats[key].channels++;
        stats[key].total++;
      }
      [p.transplant_1, p.transplant_2, p.transplant_3].forEach(planter => {
        if (planter) {
          if (!stats[planter]) stats[planter] = { name: planter, extractions: 0, channels: 0, planting: 0, total: 0 };
          stats[planter].planting++;
          stats[planter].total++;
        }
      });
    });

    setStaffStats(Object.values(stats).sort((a, b) => b.total - a.total));
  }, [patients, dateFilter, startDate, endDate]);

  const totalOps = staffStats.reduce((s, st) => s + st.extractions, 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft">جاري التحميل...</div></div>;

  return (
    <div className="animate-fade-in" data-testid="staff-stats-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">إحصائيات الموظفين</h1>
          <p className="page-subtitle">عدد العمليات لكل موظف</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-center">
          <p className="text-2xl font-bold text-emerald-700">{totalOps}</p>
          <p className="text-xs text-emerald-600">إجمالي العمليات</p>
        </div>
      </div>

      {/* Date Filter */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium text-gray-700">فلترة بالتاريخ:</span>
          {[
            { key: "year", label: "هذه السنة" },
            { key: "month", label: "هذا الشهر" },
            { key: "lastMonth", label: "الشهر السابق" },
            { key: "range", label: "فترة محددة" },
            { key: "all", label: "الكل" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setDateFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${dateFilter === f.key ? "bg-emerald-600 text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {dateFilter === "range" && (
          <div className="flex items-center gap-3 mt-3">
            <span className="text-sm text-gray-500">من:</span>
            <input type="date" className="form-input text-sm py-1 px-2 w-auto" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="text-sm text-gray-500">إلى:</span>
            <input type="date" className="form-input text-sm py-1 px-2 w-auto" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {staffStats.map((staff, index) => (
          <div key={index} className="card p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white font-bold">
                {staff.name.charAt(0)}
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{staff.name}</h3>
                <p className="text-xs text-gray-400">{staff.total} عملية</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">الاقتطاف</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 bg-blue-200 rounded-full" style={{ width: `${Math.max(staff.extractions * 8, 4)}px` }} />
                  <span className="font-bold text-blue-700 w-6 text-left">{staff.extractions}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">فتح القنوات</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 bg-purple-200 rounded-full" style={{ width: `${Math.max(staff.channels * 8, 4)}px` }} />
                  <span className="font-bold text-purple-700 w-6 text-left">{staff.channels}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">الزراعة</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 bg-emerald-200 rounded-full" style={{ width: `${Math.max(staff.planting * 8, 4)}px` }} />
                  <span className="font-bold text-emerald-700 w-6 text-left">{staff.planting}</span>
                </div>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-700 font-medium">الإجمالي</span>
                <span className="font-bold text-lg text-emerald-600">{staff.total}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {staffStats.length === 0 && (
        <div className="empty-state card p-8">
          <Users className="empty-state-icon" />
          <h3>لا توجد بيانات</h3>
          <p>لا توجد عمليات في هذه الفترة</p>
        </div>
      )}
    </div>
  );
};

export default StaffStatsPage;
